<template>
  <!-- eslint-disable max-len -->
  <div class="login__container">
    <transition name="fade">
      <template v-if="showDevWidget">
        <dev-widget @close="toggleDevWidget">
        </dev-widget>
      </template>
    </transition>
    <div class="login-benefits">
      <div class="login-benefits__background" />
      <div class="login-benefits__bioconnect">
        <a
          target="_blank"
          href="https://www.bioconnect.com"
        >
          <img src="../../assets/logos/bioconnect_light.png" />
        </a>
        Enabling Trust in the Connected World
      </div>
      <img
        class="login-benefits__logo"
        src="../../assets/logos/link_logo.svg"
      />
      <div class="login-benefits__description">
        <p class="m-0">
          Transforming the traditional.
        </p>
        <p>A familiar access experience, with the added security of multi-factor authentication.</p>
      </div>
      <div class="login-banner">
        <div class="login-banner__item">
          <span
            class="login-banner__icon"
            v-html="guideIcon"
          />
          <a
            class="login-banner__link"
            target="_blank"
            href="https://bioconnect.com/link/install/"
          >
            User Guide
          </a>
        </div>
        <div class="login-banner__item">
          <span
            class="login-banner__icon"
            v-html="searchIcon"
          />
          <a
            class="login-banner__link"
            target="_blank"
            href="https://bioconnect.com/add-a-device/"
          >
            How to add a device
          </a>
        </div>
        <div class="login-banner__item">
          <img
            :src="networkingIcon"
            class="help-banner__icon"
          />
          <a
            class="login-banner__link"
            target="_blank"
            href="https://bioconnect.com/resources/install-guides/device-networking/"
          >
            Device Networking
          </a>
        </div>
      </div>
      <div class="login-footer">
        <router-link
          id="release-notes"
          :to="{ name: 'releaseNotes' }"
          class="login-footer__item"
        >
          Release Notes
        </router-link>
        <router-link
          id="system-health"
          :to="{ name: 'status' }"
          class="login-footer__item"
        >
          System Health
        </router-link>
      </div>
    </div>
    <div class="login-form">
      <template v-if="mode === 'request-reset'">
        <request-reset
          @navigate-login="navigateLogin"
          @request-reset="submitResetRequest"
        >
        </request-reset>
      </template>
      <template v-else-if="mode === 'reset'">
        <reset-password
          :error="errorMessage"
          :loading="loading"
          :reset-token="$attrs.resetToken"
          @navigate-login="navigateLogin"
          @reset-password="submitResetPassword"
        >
        </reset-password>
      </template>
      <template v-else>
        <div class="login-form-container">
          <template v-if="isInternetExplorer">
            <callout
              type="danger"
              class="mb-3"
            >
              <template v-slot:message>
                Some functionality might not work as intended on your browser! For the best
                experience using the BioConnect Link Manager, please use Google Chrome or Firefox.
              </template>
            </callout>
          </template>
          <div class="login-form__title">
            Sign In
          </div>
          <template v-show="error">
            <div class="login__error-message">
              {{ errorMessage }}
            </div>
          </template>
        </div>
        <div
          v-shortkey="['enter']"
          class="login-form-container"
          @shortkey="submitLogin"
        >
          <div
            v-show="!urlSubDomain"
            class="login-form__item"
          >
            <label class="login-form__label"> Subdomain </label>
            <input
              id="subDomain"
              ref="subDomain"
              v-model.trim="credentials.subDomain"
              :maxlength="inputMaxLength"
              aria-label="Subdomain field"
              type="text"
              class="login-form__field"
            />
          </div>
          <div class="login-form__item">
            <label class="login-form__label"> Username </label>
            <input
              id="username"
              ref="username"
              v-model.trim="credentials.username"
              :maxlength="inputMaxLength"
              aria-label="Username field"
              type="text"
              class="login-form__field"
            />
          </div>
          <div class="login-form__item">
            <label class="login-form__label"> Password </label>
            <input
              id="password"
              ref="password"
              v-model.trim="credentials.password"
              :maxlength="inputMaxLength"
              aria-label="Password field"
              type="password"
              class="login-form__field"
            />
            <div
              id="forgot-password"
              class="login__link login__forgot-password"
              @click="navigateRequestReset"
            >
              Forgot Password?
            </div>
          </div>
        </div>
        <div class="login-button-container">
          <template v-if="loading">
            <loader :size="50" />
          </template>
          <template v-else>
            <button
              id="submit-login"
              class="btn"
              type="submit"
              @click="submitLogin"
            >
              Sign In
            </button>
          </template>
        </div>
        <div
          id="terms"
          class="login-terms"
        >
          By signing in, you accept our
          <a
            href="https://bioconnect.com/legal-2/platform-terms-conditions/"
            target="_blank"
          >Terms of Service.</a>
        </div>
        <div
          v-if="isDevEnv"
          class="login__developer"
          title="Developer Settings"
          @click="toggleDevWidget"
        >
          &#127849;
        </div>
      </template>
    </div>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import Loader from '@/components/loader'
import ResetPasswordRequest from '@/components/views/resetPasswordRequest'
import ResetPassword from '@/components/views/resetPassword'
import DevWidget from '@/components/devWidget.vue'
import Callout from '@/components/callout.vue'

import passwordQueries from '@/graphql/password.js'
import {ENV} from '@/config'

import feather from 'feather-icons'
import networkingIcon from '@/assets/routernetwork.svg'

export default {
  name: 'Login',
  components: {
    loader: Loader,
    requestReset: ResetPasswordRequest,
    resetPassword: ResetPassword,
    DevWidget,
    Callout,
  },
  mixins: [passwordQueries],
  props: ['mode'],
  data() {
    return {
      showDevWidget: false,
      loading: false,
      errorMessage: '',
      inputMaxLength: 64,
      credentials: {
        subDomain: '',
        username: '',
        password: '',
      },
      healthIcon: feather.icons.activity.toSvg({width: '1.5em'}),
      guideIcon: feather.icons['book-open'].toSvg({width: '1.5em'}),
      searchIcon: feather.icons.search.toSvg({width: '1.5em'}),
      networkingIcon: networkingIcon,
    }
  },
  computed: {
    urlSubDomain() {
      const _host = window.location.host
      const [_subdomain] = _host.split('.')
      const _ignoreDomains = [
        'n3xt',
        'link',
        'link-dev',
        'link-qa',
        'link-stage',
        'localhost:8080',
        'bc-local',
        'www',
        'admin',
        'beta',
        'blog',
        'ftp',
        'imap',
        'mail',
        'pop',
        'pop3',
        'sftp',
        'smtp',
        'ssl',
      ]
      const _shouldIgnore = _ignoreDomains.some((domain) => {
        return domain === _subdomain
      })
      return _shouldIgnore ? null : _subdomain
    },
    isDevEnv() {
      return ENV.environment === 'development'
    },
    isInternetExplorer() {
      const _userAgent = window.navigator.userAgent
      return /MSIE|Trident/.test(_userAgent)
    },
  },
  methods: {
    async submitLogin() {
      try {
        this.loading = true
        this.errorMessage = null
        const {subDomain, username, password} = this.credentials
        const loginPayload = {
          subDomain: this.urlSubDomain ? this.urlSubDomain : subDomain.toLowerCase(),
          username: username.toLowerCase(),
          password,
        }

        await this.login(loginPayload)

        if (this.$attrs.query) {
          this.$router.push({name: 'devices', params: {newDevice: this.$attrs.query}})
        } else {
          this.$router.push({name: 'users'})
        }
      } catch (error) {
        this.setLoginErrorMessage(error)
      } finally {
        this.loading = false
      }
    },
    async submitResetRequest(resetRequest) {
      try {
        await this.resetPasswordRequest(resetRequest)
        this.displayAlert('success', 'Submitted request to reset password.')
      } catch (error) {
        console.log('Unable to submit request', error)
      }
    },
    async submitResetPassword(newPassword, resetToken) {
      this.errorMessage = ''
      this.loading = true
      try {
        await this.resetPasswordMutation(newPassword, resetToken)
        this.loading = false
        this.displayAlert('success', 'Password has been reset!')
        this.navigateLogin()
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to reset password.')
        this.setPasswordErrorMessage(error)
      }
    },
    setLoginErrorMessage(error) {
      try {
        const _errorResponse = error.response.data
        const _message = _errorResponse.split(':')
        this.errorMessage = _message[1].replace(/"/gi, '')
      } catch (error) {
        this.errorMessage = 'Unable to sign in'
        console.log(error)
      }
    },
    setPasswordErrorMessage(error) {
      const _errorIndex = 3
      if (error && error.message) {
        const _errorResponse = error.message
        const _message = _errorResponse.split(':')
        this.errorMessage = _message[_errorIndex].replace(/"/gi, '')
      } else {
        this.errorMessage = 'Please check your password matches the criteria.'
      }
    },
    navigateRequestReset() {
      this.$router.push({name: 'login', params: {mode: 'request-reset'}})
    },
    navigateLogin() {
      this.errorMessage = ''
      this.$router.push({name: 'login'})
    },
    toggleDevWidget() {
      this.showDevWidget = !this.showDevWidget
    },
  },
}
</script>

<style lang="scss">
@import "@/scss/_login.scss";
@import "@/scss/_resetPassword.scss";
</style>
